<template>

  <div class="datepicker" v-on-clickaway="onClose">
    <input type="text" class="datepicker__input" style="display: none">
    <button class="datepicker__show" @click.stop="onToggle">
      <span v-if="taskDeadline !== 0 && useInSeconds" class="flex-none">{{taskDeadline * 1000 | moment('D MMM YYYY')}}</span>
      <span v-if="taskDeadline !== 0 && !useInSeconds" class="flex-none">{{taskDeadline | moment('D MMM')}}</span>
      <span v-if="taskDeadline == 0" class="flex-none">None</span>
      <svg class="icon icon--chevron-down ml4" width="8" height="4" viewBox="0 0 8 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.99896 2.84506L0.927172 0.141524C0.715077 -0.0452716 0.371131 -0.0452167 0.159037 0.141609C-0.0530122 0.328181 -0.0530122 0.630769 0.159037 0.817645L3.6159 3.86004C3.82805 4.04669 4.17204 4.04661 4.38404 3.86004C4.39318 3.85198 4.40193 3.84372 4.41028 3.83525L7.84081 0.815987C8.05309 0.62933 8.05305 0.326607 7.84076 0.139951C7.62871 -0.0466503 7.28497 -0.0466503 7.07258 0.139951L3.99896 2.84506Z"/>
      </svg>
    </button>
    <!-- datepicker__block -->
    <div class="datepicker__block" v-if="showDatepicker">
      <!-- calendar-header -->
      <div class="datepicker__header">
        <div class="datepicker__header-row">
          <i class="datepicker__control icon-chevron-left" @click="setPreviousMonth()"></i>
          <div class="datepicker__title">
            {{ monthYearFormatted }}
          </div>
          <i class="datepicker__control icon-chevron-right" @click="setNextMonth()"></i>
        </div>
        <div class="datepicker__days">
          <div class="datepicker__day-title" v-for="day in days" :key="day">
            {{ day | trimThreeChar }}
          </div>
        </div>
      </div><!-- calendar-header END -->
      <!-- calendar-body -->
      <div class="datepicker__body">
        <div class="datepicker__body-row" :class="{ 'swipe-left': swipeLeft, 'swipe-right': swipeRight }"
          v-for="(row, index) in calendarDays" :key="index">
          <div class="datepicker__day" :class="{disabled: isDisabled(cell), selected: isSelected(cell)}"
            v-for="(cell, index) in row" :key="index" @click="selectDay(cell)">
            {{ cell.day }}
          </div>
        </div>
      </div><!-- calendar-body END -->
    </div><!-- datepicker__block END -->
  </div>

</template>

<script>
import { mixin as clickaway } from 'vue-clickaway'
export default {
  name: 'Datepicker',
  props: {
    taskDeadline: {
      type: Number
    },
    useInSeconds: {
      type: Boolean,
      default: false
    }
  },
  mixins: [ clickaway ],
  data () {
    return {
      date: '',
      activeDate: '201901',
      activeDay: '',
      showDatepicker: false,
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      months: {
        '01': 'January',
        '02': 'February',
        '03': 'March',
        '04': 'April',
        '05': 'May',
        '06': 'June',
        '07': 'July',
        '08': 'August',
        '09': 'September',
        '10': 'October',
        '11': 'November',
        '12': 'December'
      },
      calendarDays: [],
      swipeLeft: false,
      swipeRight: false
    }
  },
  filters: {
    trimThreeChar (item) {
      return item.substr(0, 3);
    }
  },
  computed: {
    monthYearFormatted () {
      return this.months[this.activeDate.substr(4, 2)] + ' ' + this.activeDate.substr(0, 4)
    }
  },
  methods: {
    onToggle () {
      this.showDatepicker = !this.showDatepicker
    },
    onClose () {
      this.showDatepicker = false
    },
    getCurrentDate () {
      return new Date().toJSON().slice(0, 10).replace(/-/g, '/')
    },
    getCurrentDay () {
      return new Date().getDate()
    },
    getCurrentMonth () {
      return ("0" + (new Date().getMonth() + 1)).slice(-2)
    },
    getPreviousMonth (month) {
      if (parseInt(month) == 1) { 
        return '12' 
      } else { 
        return ("0" + (parseInt(month) - 1)).slice(-2) 
      }
    },
    getNextMonth (month) {
      if (parseInt(month) == 12) { 
        return '01' 
      } else { 
        return ("0" + (parseInt(month) + 1)).slice(-2) 
      }
    },
    getCurrentYear () {
      return new Date().getFullYear()
    },
    getNumberOfDaysInMonth (year, month) {
      return new Date(year, month, 0).getDate()
    },
    getNumberOfDaysInPreviousMonth (year, month) {
      if (month == '01') { 
        return new Date(year - 1, this.getPreviousMonth(month), 0).getDate() 
      } else { 
        return new Date(year, this.getPreviousMonth(month), 0).getDate() 
      }
    },
    getNumberOfDaysInNextMonth () {
      if (this.getNextMonth() == '12') { 
        return new Date(this.getCurrentYear() + 1, this.getNextMonth(), 0).getDate() 
      } else { 
        return new Date(this.getCurrentYear(), this.getNextMonth(), 0).getDate() 
      }
    },
    getFirstDayOfMonth (year, month) {
      if (new Date(year, parseInt(month) - 1, 1).getDay() - 1 == -1) { 
        return 6 
      } else { 
        return new Date(year, parseInt(month) - 1, 1).getDay() - 1 
      }
    },
    getLastDayOfMonth (year, month) {
      return new Date(year, parseInt(month) + 1, 0).getDay()
    },
    populateCalendar (year, month) {
      this.calendarDays = []
      const firstDayOfMonth = this.getFirstDayOfMonth(year, month)
      const numberOfRows = Math.ceil(this.getNumberOfDaysInMonth(year, month) / 7)
      let dayNumber = 1
      let increment = 1
      for (let x = 0; x <= numberOfRows; x++) {
        let decrement = 0
        let row = []

        if (x == 0) {
          for (let i = 0; i < firstDayOfMonth; i++) {
            if (parseInt(month) == 1) { 
              row.push({
                day: this.getNumberOfDaysInPreviousMonth(year, month) - decrement, 
                month: this.getPreviousMonth(month), 
                year: parseInt(year) - 1
              }) 
            } else { 
              row.push({ 
                day: this.getNumberOfDaysInPreviousMonth(year, month) - decrement, 
                month: this.getPreviousMonth(month), 
                year: parseInt(year)
              }) 
            }
            decrement++
          }
          row.reverse()
        }
        for (let y = row.length; y < 7 && y < this.getNumberOfDaysInMonth(year, month); y++) {
          if (dayNumber <= this.getNumberOfDaysInMonth(year, month)) {
            row.push({day: dayNumber, month: month, year: year})
            dayNumber++
          } else {
            for (let j = row.length; j < 7; j++) {
              if (parseInt(month) == 12) { 
                row.push({
                  day: increment, 
                  month: this.getNextMonth(month), 
                  year: parseInt(year) + 1
                }) 
              } else { 
                row.push({
                  day: increment, 
                  month: this.getNextMonth(month), 
                  year: year
                }) 
              }
              increment++
            }
          } 
        }
        this.calendarDays.push(row)
      }
    },
    setPreviousMonth () {
      let activeYear = this.activeDate.substr(0, 4)
      let activeMonth = this.activeDate.substr(4, 2)
      if (parseInt(activeMonth) == 1) {
        activeYear = parseInt(activeYear) - 1
        activeMonth = '12'
      } else {
        activeMonth = ('0' + (parseInt(activeMonth) - 1)).slice(-2)
      }
      this.activeDate = activeYear + activeMonth
      this.populateCalendar(activeYear, activeMonth)
      this.animeSwipeRight()
    },
    setNextMonth () {
      let activeYear = this.activeDate.substr(0, 4)
      let activeMonth = this.activeDate.substr(4, 2)
      if (parseInt(activeMonth) == 12) {
        activeYear = parseInt(activeYear) + 1
        activeMonth = '01'
      } else {
        activeMonth = ('0' + (parseInt(activeMonth) + 1)).slice(-2)
      }
      this.activeDate = activeYear + activeMonth
      this.populateCalendar(activeYear, activeMonth)
      this.animeSwipeLeft()
    },
    selectDay (cell) {
      if (cell.month == this.getPreviousMonth(this.activeDate.substr(4, 2))) {
        this.setPreviousMonth()
      } else if (cell.month == this.getNextMonth(this.activeDate.substr(4, 2))) {
        this.setNextMonth()
      }
      this.activeDay = ("0" + cell.day).slice(-2) + cell.month + cell.year
      this.date = ("0" + cell.day).slice(-2) + '/' + cell.month + '/' + cell.year
      this.showDatepicker = false 
      const day = ("0" + cell.day).slice(-2)
      const deadline = this.useInSeconds
        ? this.$moment(`${cell.year}-${cell.month}-${day} 23:59:59.000`).utc().unix()
        : this.$moment(`${cell.year}-${cell.month}-${day} 23:59:59.000`).utc().unix() * 1000
      this.$emit("updateDeadline", deadline)
    },
    isSelected (cell) {
      if (("0" + cell.day).slice(-2) + cell.month + cell.year == this.activeDay) {
        return true
      }
    },
    isDisabled (cell) {
      if (parseInt(cell.month) !== parseInt(this.activeDate.substr(4, 2))) {
        return true
      }
    },
    animeSwipeLeft () {
      this.swipeLeft = true
      setTimeout(() => {
        this.swipeLeft = false
      }, 300)
    },
    animeSwipeRight () {
      this.swipeRight = true
      setTimeout(() => {
        this.swipeRight = false
      }, 300)
    }
  },
  created () {
    this.populateCalendar(this.getCurrentYear(), this.getCurrentMonth())
    this.activeDate = this.getCurrentYear() + this.getCurrentMonth()
  }
}

</script>


// WEBPACK FOOTER //
// DatePicker.vue?009e4387